import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { BIAS_ZONE_EXTENT, computeDots, PopulationDot, PopulationWidgetParams } from './population.model';

interface ProportionSegment {
  label: string;
  percentage: number;
  color: string;
}

interface ProportionRow {
  label: string;
  segments: ProportionSegment[];
}

@Component({
  selector: 'dojo-population-widget',
  standalone: true,
  template: `
    <div class="flex h-full flex-col items-center justify-center bg-neutral-50 p-3">
      <!-- Legend -->
      @if (params().demographicCategories; as categories) {
        <div class="mb-2 flex flex-wrap items-center justify-center gap-3">
          @for (cat of categories; track cat.label) {
            <span class="flex items-center gap-1">
              <span class="h-2.5 w-2.5 rounded-full" [style.background]="cat.color"></span>
              <span class="text-[10px] text-neutral-600 sm:text-xs">{{ cat.label }} ({{ cat.percentage }}%)</span>
            </span>
          }
        </div>
      }

      <!-- Dot grid -->
      <div class="relative aspect-square min-h-0 w-full max-w-[320px] rounded-lg border border-neutral-200 bg-white">
        <!-- Stratified divider -->
        @if (params().showStratifiedDivider && params().demographicCategories; as categories) {
          <div
            class="absolute inset-y-0 border-l-2 border-dashed border-neutral-300"
            [style.left.%]="categories[0].percentage"
          ></div>
          @for (cat of categories; track cat.label; let first = $first) {
            <div
              class="absolute bottom-1 text-center"
              [style.left.%]="first ? 0 : categories[0].percentage"
              [style.width.%]="cat.percentage"
            >
              <span class="text-[10px] text-neutral-400">{{ cat.label }} — {{ cat.percentage }}%</span>
            </div>
          }
        }

        <svg viewBox="0 0 100 100" class="h-full w-full" preserveAspectRatio="xMidYMid meet">
          <!-- Bias zone (rendered inside SVG for coordinate alignment) -->
          @if (params().showBiasZone) {
            <rect
              x="0"
              y="0"
              [attr.width]="biasZoneExtent"
              [attr.height]="biasZoneExtent"
              fill="rgba(254, 202, 202, 0.2)"
              stroke="#fca5a5"
              stroke-width="0.5"
              stroke-dasharray="2 2"
              rx="1"
            />
          }
          @for (dot of dots(); track dot.id) {
            <circle
              [attr.cx]="dot.cx"
              [attr.cy]="dot.cy"
              [attr.r]="dot.r"
              [style.fill]="dot.color"
              [style.opacity]="dot.opacity"
              style="transition: all 0.5s ease-in-out"
            />
          }
        </svg>
      </div>

      <!-- Population count -->
      <span class="mt-3 text-[10px] text-neutral-400 sm:text-xs">Population: {{ params().populationSize }}</span>

      <!-- Stratified proportion comparison -->
      @if (stratifiedComposition(); as comp) {
        <div class="mt-3 w-full max-w-[280px] space-y-1.5">
          @for (row of comp; track row.label) {
            <div class="flex items-center gap-2">
              <span class="w-16 text-right text-[10px] text-neutral-500">{{ row.label }}</span>
              <div class="flex h-5 flex-1 overflow-hidden rounded-full">
                @for (seg of row.segments; track seg.label) {
                  <div
                    class="flex h-full items-center justify-center transition-all duration-500"
                    [style.width.%]="seg.percentage"
                    [style.background-color]="seg.color"
                  >
                    <span class="text-[9px] font-medium text-white">{{ seg.percentage }}%</span>
                  </div>
                }
              </div>
            </div>
          }
          <p class="mt-1 text-center text-[10px] text-neutral-400">Sample preserves population proportions</p>
        </div>
      }

      <!-- Reliability bars -->
      @if (params().reliabilityBars; as bars) {
        <div class="mt-4 w-full max-w-[280px] space-y-2">
          <p class="mb-1 text-center text-[10px] text-neutral-400">Sample Averages Comparison</p>
          @for (bar of bars; track bar.label) {
            <div class="flex items-center gap-2">
              <span class="w-24 text-right text-[10px] text-neutral-500">{{ bar.label }}</span>
              <div class="h-3 flex-1 overflow-hidden rounded-full bg-neutral-100">
                <div
                  class="h-full rounded-full transition-all duration-1000"
                  [style.width.%]="bar.width"
                  [style.background-color]="bar.color"
                ></div>
              </div>
            </div>
          }
          <p class="mt-1 text-center text-[10px] text-neutral-400">
            Random samples ≈ similar &bull; Biased sample = skewed
          </p>
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full w-full' },
})
export class PopulationWidgetComponent {
  readonly config = input.required<PopulationWidgetParams>();
  readonly biasZoneExtent = BIAS_ZONE_EXTENT;

  readonly params = computed<PopulationWidgetParams>(() => this.config());

  readonly dots = computed<PopulationDot[]>(() => computeDots(this.params()));

  /** Shows proportion comparison bars for stratified sampling. */
  readonly stratifiedComposition = computed<ProportionRow[] | undefined>(() => {
    const p = this.params();
    if (p.sampleMethod !== 'stratified' || !p.demographicCategories?.length) {
      return undefined;
    }

    const effectiveSize =
      p.samplePercentage !== null && p.samplePercentage !== undefined
        ? Math.round((p.populationSize * p.samplePercentage) / 100)
        : p.sampleSize;
    if (!effectiveSize) {
      return undefined;
    }

    const segments = p.demographicCategories.map((cat) => ({
      label: cat.label,
      percentage: cat.percentage,
      color: cat.color,
    }));

    const sampleSegments = p.demographicCategories.map((cat) => {
      const count = Math.round((effectiveSize * cat.percentage) / 100);
      return { label: `${cat.label} (${count})`, percentage: cat.percentage, color: cat.color };
    });

    return [
      { label: 'Population', segments },
      { label: `Sample (${effectiveSize})`, segments: sampleSegments },
    ];
  });
}
